import { useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
function getToken() { return localStorage.getItem('admin_token'); }

async function req(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...options.headers }
  });
  return res.json();
}

const TESTS = [
  {
    category: 'Infrastructure',
    id: 'db',
    label: 'Database connection',
    run: async () => {
      const start = Date.now();
      const res = await req('/admin/stats');
      if (res.error) throw new Error(res.error);
      return { detail: `${Date.now() - start}ms · ${res.users?.total || res.totalUsers || 0} users, ${res.transactions?.total || res.totalTx || 0} transactions` };
    }
  },
  {
    category: 'Infrastructure',
    id: 'backend',
    label: 'Railway backend reachable',
    run: async () => {
      const start = Date.now();
      const res = await fetch(`${API}/platform-settings`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return { detail: `${Date.now() - start}ms response time` };
    }
  },
  {
    category: 'Infrastructure',
    id: 'frontend',
    label: 'Frontend reachable',
    run: async () => {
      const start = Date.now();
      const res = await fetch('https://app.thunderboltapps.com');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return { detail: `${Date.now() - start}ms response time` };
    }
  },
  {
    category: 'Infrastructure',
    id: 'shorturl',
    label: 'tbolt.pro resolves',
    run: async () => {
      const res = await fetch('https://tbolt.pro', { redirect: 'follow' });
      if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
      return { detail: `Resolved — status ${res.status}` };
    }
  },
  {
    category: 'Auth & Security',
    id: 'auth_protected',
    label: 'Protected routes require auth',
    run: async () => {
      const res = await fetch(`${API}/payments/balance`);
      if (res.status !== 401) throw new Error(`Expected 401, got ${res.status}`);
      return { detail: 'Returns 401 without token' };
    }
  },
  {
    category: 'Auth & Security',
    id: 'auth_admin',
    label: 'Admin token valid',
    run: async () => {
      const res = await req('/admin/stats');
      if (res.error) throw new Error(res.error);
      return { detail: 'Admin JWT accepted' };
    }
  },
  {
    category: 'Auth & Security',
    id: 'no_private_keys',
    label: 'No wallet private keys in DB',
    run: async () => {
      const res = await req('/admin/test/check-private-keys');
      if (res.error) throw new Error(res.error);
      if (res.count > 0) throw new Error(`${res.count} users still have private keys stored`);
      return { detail: 'No private keys in DB' };
    }
  },
  {
    category: 'BTC Price',
    id: 'price_coingecko',
    label: 'CoinGecko price API',
    run: async () => {
      const start = Date.now();
      const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd');
      const data = await res.json();
      if (!data?.bitcoin?.usd) throw new Error('No price returned');
      return { detail: `$${data.bitcoin.usd.toLocaleString()} in ${Date.now() - start}ms` };
    }
  },
  {
    category: 'BTC Price',
    id: 'price_coinbase',
    label: 'Coinbase price API',
    run: async () => {
      const start = Date.now();
      const res = await fetch('https://api.coinbase.com/v2/prices/BTC-USD/spot');
      const data = await res.json();
      if (!data?.data?.amount) throw new Error('No price returned');
      return { detail: `$${parseFloat(data.data.amount).toLocaleString()} in ${Date.now() - start}ms` };
    }
  },
  {
    category: 'BTC Price',
    id: 'price_cryptocompare',
    label: 'CryptoCompare price API',
    run: async () => {
      const start = Date.now();
      const res = await fetch('https://min-api.cryptocompare.com/data/price?fsym=BTC&tsyms=USD');
      const data = await res.json();
      if (!data?.USD) throw new Error('No price returned');
      return { detail: `$${data.USD.toLocaleString()} in ${Date.now() - start}ms` };
    }
  },
  {
    category: 'CDP Treasury',
    id: 'cdp_balances',
    label: 'CDP treasury balances',
    run: async () => {
      const res = await req('/admin/vault');
      if (res.error) throw new Error(res.error);
      const usdc = res.cdpTreasury?.usdcBalance || '0';
      const sats = res.cdpTreasury?.satoshis || 0;
      return { detail: `USDC: $${usdc} · cbBTC: ${sats.toLocaleString()} sats` };
    }
  },
  {
    category: 'Balance Engine',
    id: 'balance_users',
    label: 'User balances calculate correctly',
    run: async () => {
      const res = await req('/admin/test/balance-check');
      if (res.error) throw new Error(res.error);
      if (res.negative > 0) return { detail: `${res.negative} users with negative balance`, warning: true };
      return { detail: `All ${res.checked} user balances verified` };
    }
  },
  {
    category: 'Balance Engine',
    id: 'stuck_pending',
    label: 'No stuck pending transactions',
    run: async () => {
      const res = await req('/admin/test/stuck-transactions');
      if (res.error) throw new Error(res.error);
      if (res.stuckPending > 0) return { detail: `${res.stuckPending} pending transactions older than 24h`, warning: true };
      if (res.stuckProcessing > 0) return { detail: `${res.stuckProcessing} processing cashouts older than 7 days`, warning: true };
      return { detail: 'No stuck transactions' };
    }
  },
  {
    category: 'Database Health',
    id: 'db_tables',
    label: 'All required tables exist',
    run: async () => {
      const res = await req('/admin/test/db-health');
      if (res.error) throw new Error(res.error);
      const missing = res.missing || [];
      if (missing.length > 0) throw new Error(`Missing: ${missing.join(', ')}`);
      return { detail: `All ${res.checked} tables present` };
    }
  },
  {
    category: 'Database Health',
    id: 'db_columns',
    label: 'strike_count and suspended columns exist',
    run: async () => {
      const res = await req('/admin/test/db-columns');
      if (res.error) throw new Error(res.error);
      if (!res.strikeCount) throw new Error('Missing strike_count column');
      if (!res.suspended) throw new Error('Missing suspended column');
      return { detail: 'All columns present' };
    }
  },
  {
    category: 'Database Health',
    id: 'duplicate_shortcodes',
    label: 'No duplicate short codes',
    run: async () => {
      const res = await req('/admin/test/duplicate-shortcodes');
      if (res.error) throw new Error(res.error);
      if (res.duplicates > 0) throw new Error(`${res.duplicates} duplicate short codes found`);
      return { detail: 'No duplicates' };
    }
  },
  {
    category: 'Database Health',
    id: 'contacts_table',
    label: 'Contacts table exists',
    run: async () => {
      const res = await req('/admin/test/contacts-table');
      if (res.error) throw new Error(res.error);
      if (!res.exists) throw new Error('contacts table missing');
      return { detail: `Table present · ${res.count} contacts saved` };
    }
  },
  {
    category: 'Payment Flow',
    id: 'payment_request',
    label: 'Create and delete test payment request',
    run: async () => {
      const res = await req('/admin/test/payment-request');
      if (res.error) throw new Error(res.error);
      return { detail: `Created ${res.shortCode} and deleted in ${res.ms}ms` };
    }
  },
  {
    category: 'Payment Flow',
    id: 'contact_flow',
    label: 'Create request with contact — contact_id saved',
    run: async () => {
      const res = await req('/admin/test/contact-flow');
      if (res.error) throw new Error(res.error);
      if (!res.contactSaved) throw new Error('contact_id not saved on payment request');
      if (!res.contactInDB) throw new Error('contact not saved in contacts table');
      return { detail: `Contact "${res.contactName}" saved · request linked · deleted in ${res.ms}ms` };
    }
  },
  {
    category: 'Stripe',
    id: 'stripe_key',
    label: 'Stripe API key format',
    run: async () => {
      const res = await req('/admin/test/stripe');
      if (res.error) throw new Error(res.error);
      if (res.isTest) return { detail: 'Using test key (sk_test_) — switch to live before launch', warning: true };
      if (!res.valid) throw new Error('Invalid or missing Stripe key');
      return { detail: 'Live key present and valid' };
    }
  },
  {
    category: 'Boost Simulation',
    id: 'boost_dry_run',
    label: 'Monthly boost dry run (current month)',
    run: async () => {
      const res = await req('/admin/test/boost-simulation');
      if (res.error) throw new Error(res.error);
      if (res.alreadyRan) return { detail: `Already ran for ${res.period}` };
      return { detail: `${res.eligibleUsers} users · ${(res.totalSatoshis || 0).toLocaleString()} sats · $${res.totalUsd} at $${(res.btcPrice || 0).toLocaleString()}/BTC · DRY RUN` };
    }
  },
  {
    category: 'Wave Integration',
    id: 'wave_token',
    label: 'Wave OAuth token present',
    run: async () => {
      const res = await req('/admin/test/wave');
      if (res.error) throw new Error(res.error);
      if (!res.hasToken) throw new Error('No Wave OAuth token in DB');
      if (res.expired) return { detail: 'Wave token may be expired — reconnect in Integrations', warning: true };
      return { detail: 'Wave token present' };
    }
  },
  {
    category: 'Push Notifications',
    id: 'vapid',
    label: 'VAPID keys present',
    run: async () => {
      const res = await req('/admin/test/vapid');
      if (res.error) throw new Error(res.error);
      if (!res.publicKey) throw new Error('Missing VAPID_PUBLIC_KEY');
      if (!res.privateKey) throw new Error('Missing VAPID_PRIVATE_KEY');
      return { detail: `Keys present · ${res.subscriptions} active subscriptions` };
    }
  },
];

const CATEGORIES = [...new Set(TESTS.map(t => t.category))];

function buildReport(results, elapsed, passed, failed, warnings, total) {
  const lines = [];
  const now = new Date().toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' });
  lines.push('THUNDERBOLT SYSTEM TEST REPORT');
  lines.push('Generated: ' + now);
  lines.push('Duration:  ' + (elapsed ? (elapsed/1000).toFixed(1) + 's' : '--'));
  lines.push('Results:   ' + passed + ' passed / ' + failed + ' failed / ' + warnings + ' warnings / ' + total + ' total');
  lines.push('');
  for (const cat of CATEGORIES) {
    const catTests = TESTS.filter(t => t.category === cat);
    lines.push('-- ' + cat.toUpperCase() + ' --');
    for (const test of catTests) {
      const r = results[test.id];
      if (!r) { lines.push('  o  ' + test.label); continue; }
      const icon = r.status === 'pass' ? 'PASS' : r.status === 'fail' ? 'FAIL' : r.status === 'warning' ? 'WARN' : '....';
      lines.push('  [' + icon + ']  ' + test.label);
      if (r.detail) lines.push('         ' + r.detail);
    }
    lines.push('');
  }
  if (failed > 0) {
    lines.push('-- FAILURES --');
    for (const test of TESTS) {
      const r = results[test.id];
      if (r && r.status === 'fail') {
        lines.push('  [FAIL]  ' + test.label);
        lines.push('         ERROR: ' + r.detail);
      }
    }
    lines.push('');
  }
  if (warnings > 0) {
    lines.push('-- WARNINGS --');
    for (const test of TESTS) {
      const r = results[test.id];
      if (r && r.status === 'warning') {
        lines.push('  [WARN]  ' + test.label);
        lines.push('         ' + r.detail);
      }
    }
    lines.push('');
  }
  lines.push('-- END OF REPORT --');
  return lines.join('\n');
}

export default function TestPage() {
  const [results, setResults]     = useState({});
  const [running, setRunning]     = useState(false);
  const [elapsed, setElapsed]     = useState(null);
  const [copied, setCopied]       = useState(false);

  async function runAll() {
    setResults({});
    setRunning(true);
    setElapsed(null);
    const start = Date.now();

    for (const test of TESTS) {
      setResults(prev => ({ ...prev, [test.id]: { status: 'running' } }));
      try {
        const t0  = Date.now();
        const res = await test.run();
        setResults(prev => ({
          ...prev,
          [test.id]: { status: res.warning ? 'warning' : 'pass', detail: res.detail, ms: Date.now() - t0 }
        }));
      } catch (err) {
        setResults(prev => ({
          ...prev,
          [test.id]: { status: 'fail', detail: err.message, ms: Date.now() - start }
        }));
      }
    }

    setElapsed(Date.now() - start);
    setRunning(false);
  }

  const passed   = Object.values(results).filter(r => r.status === 'pass').length;
  const failed   = Object.values(results).filter(r => r.status === 'fail').length;
  const warnings = Object.values(results).filter(r => r.status === 'warning').length;
  const total    = Object.keys(results).length;

  function copyReport() {
    navigator.clipboard.writeText(buildReport(results, elapsed, passed, failed, warnings, total));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>System Test Protocol</h1>
          <p style={s.subtitle}>Runs {TESTS.length} checks across {CATEGORIES.length} categories</p>
        </div>
        <button style={{...s.runBtn, opacity: running ? 0.6 : 1}} onClick={runAll} disabled={running}>
          {running ? 'Running...' : 'Run All Tests'}
        </button>
      </div>

      {total > 0 && (
        <div style={s.summary}>
          <div style={{...s.summaryItem, background:'#E6FAF5'}}>
            <div style={{fontSize:24, fontWeight:700, color:'#00C48C'}}>{passed}</div>
            <div style={{fontSize:12, color:'#00C48C', fontWeight:500}}>Passed</div>
          </div>
          <div style={{...s.summaryItem, background:'#FFF0EF'}}>
            <div style={{fontSize:24, fontWeight:700, color:'#FF3B30'}}>{failed}</div>
            <div style={{fontSize:12, color:'#FF3B30', fontWeight:500}}>Failed</div>
          </div>
          <div style={{...s.summaryItem, background:'#FFF9EC'}}>
            <div style={{fontSize:24, fontWeight:700, color:'#FF9500'}}>{warnings}</div>
            <div style={{fontSize:12, color:'#FF9500', fontWeight:500}}>Warnings</div>
          </div>
          <div style={{...s.summaryItem, background:'#F5F5F7'}}>
            <div style={{fontSize:24, fontWeight:700, color:'#1D1D1F'}}>{total}</div>
            <div style={{fontSize:12, color:'#6E6E73', fontWeight:500}}>Total</div>
          </div>
          {elapsed && (
            <div style={{...s.summaryItem, background:'#F5F5F7'}}>
              <div style={{fontSize:24, fontWeight:700, color:'#1D1D1F'}}>{(elapsed/1000).toFixed(1)}s</div>
              <div style={{fontSize:12, color:'#6E6E73', fontWeight:500}}>Time</div>
            </div>
          )}
        </div>
      )}

      {CATEGORIES.map(cat => {
        const catTests = TESTS.filter(t => t.category === cat);
        return (
          <div key={cat} style={s.card}>
            <div style={s.cardTitle}>{cat}</div>
            {catTests.map(test => {
              const r = results[test.id];
              return (
                <div key={test.id} style={s.testRow}>
                  <div style={s.testStatus}>
                    {!r && <div style={s.dot}/>}
                    {r?.status === 'running' && <div style={{...s.dot, background:'#007AFF'}}/>}
                    {r?.status === 'pass' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00C48C" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                    {r?.status === 'fail' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF3B30" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    )}
                    {r?.status === 'warning' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF9500" strokeWidth="2.5">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    )}
                  </div>
                  <div style={s.testInfo}>
                    <div style={s.testLabel}>{test.label}</div>
                    {r?.detail && (
                      <div style={{
                        ...s.testDetail,
                        color: r.status === 'fail' ? '#FF3B30' : r.status === 'warning' ? '#FF9500' : '#6E6E73'
                      }}>
                        {r.detail}
                      </div>
                    )}
                  </div>
                  {r?.ms && r.status !== 'running' && (
                    <div style={s.testMs}>{r.ms}ms</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {total > 0 && !running && (
        <div style={s.card}>
          <div style={s.cardTitle}>Report</div>
          <pre style={{
            background:'#1D1D1F', color:'#E5E5EA', borderRadius:12,
            padding:16, fontSize:11, lineHeight:1.8, overflowX:'auto',
            whiteSpace:'pre-wrap', wordBreak:'break-word', marginBottom:12
          }}>
            {buildReport(results, elapsed, passed, failed, warnings, total)}
          </pre>
          <button style={{
            display:'flex', alignItems:'center', gap:8,
            padding:'10px 18px',
            background: copied ? '#00C48C' : '#007AFF',
            color:'#fff', border:'none', borderRadius:10,
            fontSize:13, fontWeight:600, cursor:'pointer'
          }} onClick={copyReport}>
            {copied ? 'Copied!' : 'Copy Report'}
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  header:      { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 },
  title:       { fontSize:24, fontWeight:700, color:'#1D1D1F' },
  subtitle:    { fontSize:14, color:'#6E6E73', marginTop:4 },
  runBtn:      { display:'flex', alignItems:'center', gap:8, padding:'12px 20px', background:'#007AFF', color:'#fff', border:'none', borderRadius:12, fontSize:14, fontWeight:600, cursor:'pointer' },
  summary:     { display:'flex', gap:12, marginBottom:20 },
  summaryItem: { flex:1, borderRadius:12, padding:16, textAlign:'center' },
  card:        { background:'#fff', borderRadius:16, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  cardTitle:   { fontSize:11, fontWeight:600, color:'#AEAEB2', textTransform:'uppercase', letterSpacing:0.8, marginBottom:12 },
  testRow:     { display:'flex', alignItems:'flex-start', gap:12, padding:'10px 0', borderBottom:'1px solid #F5F5F7' },
  testStatus:  { width:20, flexShrink:0, paddingTop:1 },
  dot:         { width:8, height:8, borderRadius:'50%', background:'#E5E5EA', margin:'4px auto' },
  testInfo:    { flex:1 },
  testLabel:   { fontSize:14, fontWeight:500, color:'#1D1D1F' },
  testDetail:  { fontSize:12, marginTop:2 },
  testMs:      { fontSize:11, color:'#AEAEB2', flexShrink:0, paddingTop:2 },
};
